import { TooltipTrigger } from "@radix-ui/react-tooltip";
import { GenericTable } from "@shared/components/GenericTable";
import { useCurrency } from "@shared/selectors/globalSelectors";
import { TableColumn } from "@shared/types/table";
import { Badge } from "@shared/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@shared/ui/card";
import { Tooltip, TooltipContent, TooltipProvider } from "@shared/ui/tooltip";
import { AlertTriangle, HelpCircle } from "lucide-react";

export const OverviewTab: React.FC<{ data: any }> = ({ data }) => {
  const currency = useCurrency();
  const systemFinancialData = {
    annualDepreciation: data?.systemDepreciationSchedule?.annualDepreciation,
    depreciationToDate: data?.systemDepreciationSchedule?.depreciationToDate,
    remainingDepreciation:
      data?.systemDepreciationSchedule?.remainingDepreciation,
    nextDepreciationDate:
      data?.systemDepreciationSchedule?.nextDepreciationDate,
  };

  const endOfLifeColumns: TableColumn<any>[] = [
    {
      key: "assetName",
      header: "Asset Name",
      type: "text",
      render: (value, record) => (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="cursor-help text-blue-600 hover:underline">
                {value}
              </span>
            </TooltipTrigger>
            <TooltipContent className="max-w-xs text-sm">
              This asset is nearing its end-of-life. Its replacement will
              require an estimated capital expenditure of{" "}
              <span className="font-semibold">
                {record.estimatedReplacementCost?.toLocaleString("en-US", {
                  style: "currency",
                  currency: "USD",
                })}
              </span>
              . This information is critical for long-term capital planning.
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ),
    },
    {
      key: "remainingLife",
      header: "Remaining Life",
      type: "custom",
      render: (value: string, row: any) => <Badge>{value}</Badge>,
    },
    {
      key: "currentBookValue",
      header: "Current Book Value",
      type: "text",
      sortable: true,
    },
    {
      key: "replacementCost",
      header: "Est. Replacement Cost",
      type: "text",
      sortable: true,
      render: (value: string) => <span className="font-medium">{value}</span>,
    },
  ];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Depreciation Schedule */}
      <Card>
        <CardHeader>
          <CardTitle className="page-title">
            System Depreciation Schedule
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 mt-4">
          <div className="flex justify-between">
            <div className="flex item-center gap-2">
              <span className="text-sm text-gray-600">
                Annual Depreciation (Current Year):
              </span>
              <div>
                <Tooltip>
                  <TooltipTrigger>
                    <HelpCircle className="h-4 w-4 text-gray-400" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="max-w-xs">
                      The amount of depreciation expense allocated for the
                      current fiscal year.
                    </p>
                  </TooltipContent>
                </Tooltip>
              </div>
            </div>
            <span className="font-medium">
              {currency}{" "}
              {systemFinancialData?.annualDepreciation?.toLocaleString()}
            </span>
          </div>
          <div className="flex justify-between">
            <div className="flex item-center gap-2">
              <span className="text-sm text-gray-600">
                Depreciation To Date (Current Year):
              </span>
              <div>
                <Tooltip>
                  <TooltipTrigger>
                    <HelpCircle className="h-4 w-4 text-gray-400" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="max-w-xs">
                      The portion of this year's annual depreciation that has
                      already been recorded.
                    </p>
                  </TooltipContent>
                </Tooltip>
              </div>
            </div>
            <span className="font-medium">
              {currency}{" "}
              {systemFinancialData?.depreciationToDate?.toLocaleString()}
            </span>
          </div>
          <div className="flex justify-between">
            <div className="flex item-center gap-2">
              <span className="text-sm text-gray-600">
                Remaining Depreciation:
              </span>
              <div>
                <Tooltip>
                  <TooltipTrigger>
                    <HelpCircle className="h-4 w-4 text-gray-400" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="max-w-xs">
                      The total amount of depreciation that has yet to be
                      expensed over the system's remaining useful life.
                    </p>
                  </TooltipContent>
                </Tooltip>
              </div>
            </div>
            <span className="font-medium">
              {currency}{" "}
              {systemFinancialData?.remainingDepreciation?.toLocaleString()}
            </span>
          </div>
          <div className="flex justify-between">
            <div className="flex item-center gap-2">
              <span className="text-sm text-gray-600">
                Next Depreciation Date:
              </span>
              <div>
                <Tooltip>
                  <TooltipTrigger>
                    <HelpCircle className="h-4 w-4 text-gray-400" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="max-w-xs">
                      The date the next annual depreciation expense will be
                      officially recorded.
                    </p>
                  </TooltipContent>
                </Tooltip>
              </div>
            </div>
            <span className="font-medium">
              {systemFinancialData?.nextDepreciationDate}
            </span>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-orange-600" />
            <div>
              <CardTitle>Assets Nearing End-of-Life</CardTitle>
              <p className="text-sm text-gray-600 mt-1">
                Assets with less than 5 years of useful life remaining
              </p>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <GenericTable
            data={data?.assetsNearingEndOfLife ?? []}
            columns={endOfLifeColumns}
            rowKey="assetId"
            emptyMessage="No assets nearing end-of-life"
          />
        </CardContent>
      </Card>
    </div>
  );
};
